import 'braid-design-system/reset';
import 'src/modules/sendBeacon-polyfill';

import { ApolloProvider } from '@apollo/client/react';
import type { ClientOptions } from '@seek/ca-graphql-client';
import { device } from '@seek/ca-myprofile-utils';
import { Hubble, HubbleProvider } from '@seek/hubble';
import type { Brand } from '@seek/hubble/lib/HubbleEvent';
import { MelwaysProvider } from '@seek/melways-react';
import { metrics, setup as setupMetrics } from '@seek/metrics-js';
import { VocabProvider } from '@vocab/react';
import { BraidProvider } from 'braid-design-system';
import seekJobs from 'braid-design-system/themes/seekJobs';
import { parse } from 'cookie';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import App from 'src/components/App/App';
import { FeatureFlagWrapper } from 'src/components/FeatureFlagWrapper/FeatureFlagWrapper';
import { ConfigProvider } from 'src/context/config';
import { TrackingProvider } from 'src/context/tracking';
import { ApplicationCorrelationIdProvider } from 'src/context/use-application-correlation-id';
import { PromisesProvider } from 'src/context/use-promises';
import { StepsProvider } from 'src/context/use-steps-completed';
import { TestRecordProvider } from 'src/context/use-test-record';
import { VisitorIdProvider } from 'src/context/use-visitor-id';
import { setLanguage } from 'src/forms/seekResolver';
import { analyticsClient } from 'src/modules/analytics';
import { getAuthN } from 'src/modules/authnSingleton';
import { initBrowserLog } from 'src/modules/error-logger/browserLog';
import { createApollo, setVisitorId } from 'src/modules/graphql-client';
import { GET_USER } from 'src/modules/graphql-client/queries/user.graphql';
import { isSeekTest as checkIsSeekTest } from 'src/modules/testUtils';
import type { Config } from 'src/types';
import { getUrlCorrelationId } from 'src/utils';

// Before we navigate to the job apply success page we show a spinner. Browsers
// will cache the DOM for the apply page, which can no longer be interacted
// with. If the user presses back they will then see the cached DOM page with
// the spinner still showing.
//
// Adding an empty unload listener is enough to convince the browser that the page
// is unsafe to cache.
window.addEventListener('unload', () => {});

export default async (config: Config) => {
  const host = typeof window !== 'undefined' ? location.host : '';

  if (Boolean(host)) {
    try {
      const auth = await getAuthN();
      const result = await auth.getTestUserContext();
      window.TEST_USER = result?.testRecord ?? false;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      window.TEST_USER = false;
    }
  }

  const isSeekTest = checkIsSeekTest();

  const globalTags: string[] = [
    'team:apply-experience',
    `environment:${config.ENV}`,
    `browser-name:${device.getBrowserName()}`,
    `device-type:${device.getDeviceType()}`,
    `app-version:${config.VERSION}`,
    `seek-test:${isSeekTest}`,
  ];

  initBrowserLog(globalTags);

  setLanguage(config.LANGUAGE);
  setupMetrics({
    host: config.METRICS_URL,
    isEnabled: config.METRICS_ENABLED,
    prefix: 'ca_jobapply_frontend',
    globalTags,
  });

  metrics.sendPagePerformance();

  const apolloClient = createApollo(
    config.BRAND.toLowerCase() as ClientOptions['brand'],
    config.LOCALE,
    config.ENV,
    config.VERSION,
    config.GRAPHQL_ENDPOINT,
  );

  const loginId = await apolloClient
    .query({
      query: GET_USER,
      fetchPolicy: 'network-only',
    })
    .then((result) => result.data?.viewer?._id || '')
    .catch(() => '');

  const hubble = new Hubble({
    appName: 'ca-jobapply',
    appVersion: config.VERSION,
    brand: config.BRAND.toLowerCase() as Brand,
    country: config.LOCALE,
    displayLanguage: config.LANGUAGE,
    isProduction: config.ENV === 'production',
    loginId,
    pageName: 'application form',
    ...(isSeekTest
      ? { tags: { testRecord: 'true', testScope: 'load-test-apply' } }
      : {}),
    experience: 'candidate',
  });

  hubble.on('queue', (event) => {
    metrics.count(`hubble.event`, [`eventName:${event.name}`, 'stage:queue']);
  });

  hubble.on('send', (events) => {
    events.forEach((event) => {
      metrics.count(`hubble.event`, [`eventName:${event.name}`, 'stage:send']);
    });
  });

  hubble.on('success', (events) => {
    events.forEach((event) => {
      metrics.count(`hubble.event`, [
        `eventName:${event.name}`,
        'stage:success',
      ]);
    });
  });

  // Set visitor ID for all subsequent GraphQL requests
  const visitorId = hubble.visitorId();
  setVisitorId(visitorId);

  const generatedCorrelationId = uuid();
  const urlCorrelationId = getUrlCorrelationId();
  const appId = urlCorrelationId ? urlCorrelationId : generatedCorrelationId;
  const isNewApplication = !urlCorrelationId;

  const rootContainer = document.getElementById('app');
  const root = createRoot(rootContainer!);

  const solId = parse(window.document.cookie).sol_id || '';

  root.render(
    <MelwaysProvider.Static
      language={config.LANGUAGE}
      path={config.ROUTE}
      site={config.SITE}
    >
      <HubbleProvider hubble={hubble}>
        <VisitorIdProvider visitorId={visitorId}>
          <ApplicationCorrelationIdProvider
            applicationCorrelationId={appId}
            isNewApplication={isNewApplication}
          >
            <PromisesProvider>
              <TestRecordProvider isSeekTest={isSeekTest}>
                <ApolloProvider client={apolloClient}>
                  <VocabProvider language={config.VOCAB_LANGUAGE}>
                    <BraidProvider theme={seekJobs}>
                      <ConfigProvider config={config}>
                        <TrackingProvider client={analyticsClient}>
                          <StepsProvider>
                            <BrowserRouter>
                              <FeatureFlagWrapper
                                solId={solId}
                                debug={config.ENV !== 'production'}
                              >
                                <App />
                              </FeatureFlagWrapper>
                            </BrowserRouter>
                          </StepsProvider>
                        </TrackingProvider>
                      </ConfigProvider>
                    </BraidProvider>
                  </VocabProvider>
                </ApolloProvider>
              </TestRecordProvider>
            </PromisesProvider>
          </ApplicationCorrelationIdProvider>
        </VisitorIdProvider>
      </HubbleProvider>
    </MelwaysProvider.Static>,
  );
};
